import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { type ICurrencyType } from '@mahawi/eshop-common/src/types';
import { Space, Typography } from 'antd';
import { type ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { type Key } from 'react';

import { SPACING } from '../theme/tokens';
import { EMPTY_VALUE } from './empty-value';

export interface ProductTableColumnCalculatedEndPriceMargin {
  calculatedEndPriceMarginPercentage: number | null;
  calculatedEndPriceMarginEur: number | null;
  calculatedEndPriceAt: Date | null;
}

const { Text } = Typography;
dayjs.extend(relativeTime);

export const getCalculatedEndPriceMarginColumn = (
  currencyTypeEUR: ICurrencyType,
): ColumnType<object> => {
  return {
    title: (
      <>
        <span>Calculated end price margin</span>
        <br />
        <small>percentage / EUR</small>
        <br />
        <small>calculated at</small>
      </>
    ),
    dataIndex: 'calculatedEndPriceMarginPercentage',
    sorter: (
      a: ProductTableColumnCalculatedEndPriceMargin,
      b: ProductTableColumnCalculatedEndPriceMargin,
    ): number =>
      numberCompare(
        a.calculatedEndPriceMarginEur || 0,
        b.calculatedEndPriceMarginEur || 0,
      ),
    render: (
      calculatedEndPriceMarginPercentage: number | undefined,
      record: ProductTableColumnCalculatedEndPriceMargin,
    ): React.ReactElement => {
      return (
        <Space orientation="vertical" align="start" size={SPACING.sm}>
          <Text
            type={
              calculatedEndPriceMarginPercentage &&
              calculatedEndPriceMarginPercentage > 0
                ? 'success'
                : 'danger'
            }
          >
            {calculatedEndPriceMarginPercentage
              ? `${calculatedEndPriceMarginPercentage}%`
              : EMPTY_VALUE}{' '}
            /{' '}
            {record.calculatedEndPriceMarginEur
              ? formatCurrency(
                  record.calculatedEndPriceMarginEur,
                  currencyTypeEUR,
                )
              : EMPTY_VALUE}
          </Text>
          <Text>
            <small>
              {record.calculatedEndPriceAt
                ? dayjs(record.calculatedEndPriceAt).fromNow()
                : EMPTY_VALUE}
            </small>
          </Text>
        </Space>
      );
    },
    filters: [
      {
        text: 'Without calculated end price margin',
        value: 'withoutCalculatedEndPriceMargin',
      },
      {
        text: 'Margin below 0%',
        value: 'marginBelow0',
      },
      {
        text: 'Margin between 0% and 5%',
        value: 'marginBetween0And5',
      },
      {
        text: 'Margin between 5% and 10%',
        value: 'marginBetween5And10',
      },
      {
        text: 'Margin above 10%',
        value: 'marginAbove10',
      },
      {
        text: 'Margin below 1 EUR',
        value: 'marginBelow1EUR',
      },
      {
        text: 'Margin between 1 EUR and 5 EUR',
        value: 'marginBetween1EURAnd5EUR',
      },
      {
        text: 'Margin between 5 EUR and 10 EUR',
        value: 'marginBetween5EURAnd10EUR',
      },
      {
        text: 'Margin above 10 EUR',
        value: 'marginAbove10EUR',
      },
      {
        text: 'Calculated less than 1 hour ago',
        value: 'calculatedLessThan1HourAgo',
      },
      {
        text: 'Calculated today',
        value: 'calculatedToday',
      },
      {
        text: 'Calculated yesterday',
        value: 'calculatedYesterday',
      },
      {
        text: 'Calculated three days ago',
        value: 'calculatedThreeDaysAgo',
      },
      {
        text: 'Calculated this week',
        value: 'calculatedThisWeek',
      },
      {
        text: 'Calculated this month',
        value: 'calculatedThisMonth',
      },
      {
        text: 'Calculated last week',
        value: 'calculatedLastWeek',
      },
      {
        text: 'Calculated last month',
        value: 'calculatedLastMonth',
      },
      {
        text: 'Calculated more than 1 month ago',
        value: 'calculatedMoreThan1MonthAgo',
      },
      {
        text: 'Calculated yesterday to one week',
        value: 'calculatedYesterdayToOneWeek',
      },
      {
        text: 'Calculated yesterday to one month',
        value: 'calculatedYesterdayToOneMonth',
      },
    ],
    onFilter: (
      value: boolean | Key,
      record: ProductTableColumnCalculatedEndPriceMargin,
    ): boolean => {
      if (value === 'withoutCalculatedEndPriceMargin') {
        return !record.calculatedEndPriceMarginPercentage;
      }

      if (value === 'marginBelow0') {
        return !!(
          record.calculatedEndPriceMarginPercentage &&
          record.calculatedEndPriceMarginPercentage < 0
        );
      }

      if (value === 'marginBetween0And5') {
        return !!(
          record.calculatedEndPriceMarginPercentage &&
          record.calculatedEndPriceMarginPercentage >= 0 &&
          record.calculatedEndPriceMarginPercentage < 5
        );
      }

      if (value === 'marginBetween5And10') {
        return !!(
          record.calculatedEndPriceMarginPercentage &&
          record.calculatedEndPriceMarginPercentage >= 5 &&
          record.calculatedEndPriceMarginPercentage < 10
        );
      }

      if (value === 'marginAbove10') {
        return !!(
          record.calculatedEndPriceMarginPercentage &&
          record.calculatedEndPriceMarginPercentage >= 10
        );
      }

      if (value === 'marginBelow1EUR') {
        return !!(
          record.calculatedEndPriceMarginEur &&
          record.calculatedEndPriceMarginEur < 1
        );
      }

      if (value === 'marginBetween1EURAnd5EUR') {
        return !!(
          record.calculatedEndPriceMarginEur &&
          record.calculatedEndPriceMarginEur >= 1 &&
          record.calculatedEndPriceMarginEur < 5
        );
      }

      if (value === 'marginBetween5EURAnd10EUR') {
        return !!(
          record.calculatedEndPriceMarginEur &&
          record.calculatedEndPriceMarginEur >= 5 &&
          record.calculatedEndPriceMarginEur < 10
        );
      }

      if (value === 'marginAbove10EUR') {
        return !!(
          record.calculatedEndPriceMarginEur &&
          record.calculatedEndPriceMarginEur >= 10
        );
      }

      if (value === 'calculatedLessThan1HourAgo') {
        return !!(
          record.calculatedEndPriceAt &&
          dayjs(record.calculatedEndPriceAt).isAfter(
            dayjs().subtract(1, 'hour'),
          )
        );
      }

      if (value === 'calculatedToday') {
        return !!(
          record.calculatedEndPriceAt &&
          dayjs(record.calculatedEndPriceAt).isSame(dayjs(), 'day')
        );
      }

      if (value === 'calculatedYesterday') {
        return !!(
          record.calculatedEndPriceAt &&
          dayjs(record.calculatedEndPriceAt).isSame(
            dayjs().subtract(1, 'day'),
            'day',
          )
        );
      }

      if (value === 'calculatedThisWeek') {
        return !!(
          record.calculatedEndPriceAt &&
          dayjs(record.calculatedEndPriceAt).isSame(dayjs(), 'week')
        );
      }

      if (value === 'calculatedThisMonth') {
        return !!(
          record.calculatedEndPriceAt &&
          dayjs(record.calculatedEndPriceAt).isSame(dayjs(), 'month')
        );
      }

      if (value === 'calculatedLastWeek') {
        return !!(
          record.calculatedEndPriceAt &&
          dayjs(record.calculatedEndPriceAt).isSame(
            dayjs().subtract(1, 'week'),
            'week',
          )
        );
      }

      if (value === 'calculatedLastMonth') {
        return !!(
          record.calculatedEndPriceAt &&
          dayjs(record.calculatedEndPriceAt).isSame(
            dayjs().subtract(1, 'month'),
            'month',
          )
        );
      }

      if (value === 'calculatedMoreThan1MonthAgo') {
        return !!(
          record.calculatedEndPriceAt &&
          dayjs(record.calculatedEndPriceAt).isBefore(
            dayjs().subtract(1, 'month'),
          )
        );
      }

      if (value === 'calculatedYesterdayToOneWeek') {
        return !!(
          record.calculatedEndPriceAt &&
          dayjs(record.calculatedEndPriceAt).isAfter(
            dayjs().subtract(1, 'week'),
          ) &&
          dayjs(record.calculatedEndPriceAt).isBefore(
            dayjs().subtract(1, 'day'),
          )
        );
      }

      if (value === 'calculatedYesterdayToOneMonth') {
        return !!(
          record.calculatedEndPriceAt &&
          dayjs(record.calculatedEndPriceAt).isAfter(
            dayjs().subtract(1, 'month'),
          ) &&
          dayjs(record.calculatedEndPriceAt).isBefore(
            dayjs().subtract(1, 'day'),
          )
        );
      }

      return false;
    },
    width: '10%',
  };
};
